/**
 * Coach Service V2
 *
 * Simplified coach system:
 * - Anyone can invite anyone as a coach
 * - Coach profiles are OPTIONAL (for displaying coach info)
 * - coach_id in relationships references auth.users directly
 */

import { supabase, getCachedUser } from './supabaseService';

// =====================================================
// COACH PROFILE MANAGEMENT (OPTIONAL)
// =====================================================

export const coachProfile = {
  /**
   * Create a new coach profile for the current user (OPTIONAL)
   */
  async create(profileData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_profiles')
      .insert({
        user_id: user.id,
        display_name: profileData.displayName,
        coach_type: profileData.coachType || 'fitness',
        bio: profileData.bio || null,
        specializations: profileData.specializations || [],
        is_accepting_clients: profileData.isAcceptingClients ?? true,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update the current user's coach profile
   */
  async update(profileData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_profiles')
      .update({
        display_name: profileData.displayName,
        bio: profileData.bio,
        specializations: profileData.specializations,
        is_accepting_clients: profileData.isAcceptingClients,
      })
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get the current user's coach profile (may not exist)
   */
  async getOwn() {
    const user = await getCachedUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('coach_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching own coach profile:', error);
      return null;
    }
    return data;
  },

  /**
   * Get a coach profile by user ID
   */
  async getByUserId(userId) {
    const { data, error } = await supabase
      .from('coach_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching coach profile by userId:', error);
      return null;
    }
    return data;
  },

  /**
   * Delete the current user's coach profile
   */
  async delete() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('coach_profiles')
      .delete()
      .eq('user_id', user.id);

    if (error) throw error;
  },
};

// =====================================================
// USER SEARCH (FOR INVITING AS COACH)
// =====================================================

export const userSearch = {
  /**
   * Search users to invite as coach
   */
  async searchForInvite(searchQuery) {
    const user = await getCachedUser();
    if (!user) return [];

    const { data, error } = await supabase
      .rpc('search_users_for_coach_invite', {
        p_search: searchQuery || '',
        p_exclude_user_id: user.id,
      });

    if (error) throw error;
    return data || [];
  },
};

// =====================================================
// CLIENT-SIDE RELATIONSHIP MANAGEMENT
// =====================================================

export const clientRelationships = {
  /**
   * Invite a user as coach (coach_id is now user.id, not coach_profiles.id)
   */
  async inviteCoach(coachUserId, permissions = {}) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    // First check if there's an existing relationship (might be revoked/rejected)
    const { data: existing } = await supabase
      .from('coach_client_relationships')
      .select('id, status')
      .eq('client_id', user.id)
      .eq('coach_id', coachUserId)
      .single();

    let data, error;

    if (existing) {
      // Reactivate existing relationship
      const result = await supabase
        .from('coach_client_relationships')
        .update({
          status: 'pending',
          can_view_workouts: permissions.canViewWorkouts ?? true,
          can_view_analytics: permissions.canViewAnalytics ?? true,
          can_view_progress_photos: permissions.canViewProgressPhotos ?? false,
          can_view_body_measurements: permissions.canViewBodyMeasurements ?? false,
          invited_at: new Date().toISOString(),
          accepted_at: null,
          revoked_at: null,
        })
        .eq('id', existing.id)
        .select()
        .single();

      data = result.data;
      error = result.error;
    } else {
      // Create new relationship
      const result = await supabase
        .from('coach_client_relationships')
        .insert({
          client_id: user.id,
          coach_id: coachUserId,
          status: 'pending',
          can_view_workouts: permissions.canViewWorkouts ?? true,
          can_view_analytics: permissions.canViewAnalytics ?? true,
          can_view_progress_photos: permissions.canViewProgressPhotos ?? false,
          can_view_body_measurements: permissions.canViewBodyMeasurements ?? false,
        })
        .select()
        .single();

      data = result.data;
      error = result.error;
    }

    if (error) throw error;

    // Fetch coach info (profile + optional coach_profiles)
    const coachInfo = await getCoachInfo(coachUserId);
    return { ...data, coach_info: coachInfo };
  },

  /**
   * Update permissions for a coach relationship
   */
  async updatePermissions(relationshipId, permissions) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .update({
        can_view_workouts: permissions.canViewWorkouts,
        can_view_analytics: permissions.canViewAnalytics,
        can_view_progress_photos: permissions.canViewProgressPhotos,
        can_view_body_measurements: permissions.canViewBodyMeasurements,
      })
      .eq('id', relationshipId)
      .eq('client_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Revoke a coach's access
   */
  async revokeAccess(relationshipId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .update({
        status: 'revoked',
        revoked_at: new Date().toISOString(),
      })
      .eq('id', relationshipId)
      .eq('client_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get all coaches for the current user (client)
   */
  async getMyCoaches() {
    const user = await getCachedUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .select('*')
      .eq('client_id', user.id)
      .neq('status', 'revoked')
      .order('invited_at', { ascending: false });

    if (error) throw error;

    // Fetch coach info for each relationship
    const withCoachInfo = await Promise.all(
      (data || []).map(async (rel) => {
        const coachInfo = await getCoachInfo(rel.coach_id);
        return { ...rel, coach_info: coachInfo };
      })
    );

    return withCoachInfo;
  },

  /**
   * Delete a relationship completely
   */
  async delete(relationshipId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('coach_client_relationships')
      .delete()
      .eq('id', relationshipId)
      .eq('client_id', user.id);

    if (error) throw error;
  },
};

// =====================================================
// COACH-SIDE RELATIONSHIP MANAGEMENT
// =====================================================

export const coachRelationships = {
  /**
   * Get pending invitations for the current user (as coach)
   */
  async getPendingInvitations() {
    const user = await getCachedUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .select('*')
      .eq('coach_id', user.id) // Now directly using user.id
      .eq('status', 'pending')
      .order('invited_at', { ascending: false });

    if (error) throw error;

    // Fetch client info using helper (handles RLS)
    const withClientInfo = await Promise.all(
      (data || []).map(async (rel) => {
        const clientInfo = await getClientInfo(rel.client_id);
        return { ...rel, client: clientInfo };
      })
    );

    return withClientInfo;
  },

  /**
   * Accept an invitation
   */
  async acceptInvitation(relationshipId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .update({
        status: 'accepted',
        accepted_at: new Date().toISOString(),
      })
      .eq('id', relationshipId)
      .eq('coach_id', user.id) // Verify ownership
      .select('*')
      .single();

    if (error) throw error;

    // Fetch client info using helper (handles RLS)
    const clientInfo = await getClientInfo(data.client_id);
    return { ...data, client: clientInfo };
  },

  /**
   * Reject an invitation
   */
  async rejectInvitation(relationshipId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .update({
        status: 'rejected',
      })
      .eq('id', relationshipId)
      .eq('coach_id', user.id) // Verify ownership
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get all clients for the current user (as coach)
   */
  async getMyClients() {
    const user = await getCachedUser();
    console.log('[coachService.getMyClients] Current user:', user?.id);
    if (!user) return [];

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .select('*')
      .eq('coach_id', user.id) // Now directly using user.id
      .eq('status', 'accepted')
      .order('accepted_at', { ascending: false });

    console.log('[coachService.getMyClients] Query result:', { data, error });

    if (error) throw error;

    // Fetch client info using helper (handles RLS)
    const withClientInfo = await Promise.all(
      (data || []).map(async (rel) => {
        const clientInfo = await getClientInfo(rel.client_id);
        return { ...rel, client: clientInfo };
      })
    );

    console.log('[coachService.getMyClients] With client info:', withClientInfo);
    return withClientInfo;
  },

  /**
   * Get all coaches for the current user (as client)
   */
  async getMyCoaches() {
    const user = await getCachedUser();
    console.log('[coachService.getMyCoaches] Current user:', user?.id);
    if (!user) return [];

    const { data, error } = await supabase
      .from('coach_client_relationships')
      .select('*')
      .eq('client_id', user.id)
      .eq('status', 'accepted')
      .order('accepted_at', { ascending: false });

    console.log('[coachService.getMyCoaches] Query result:', { data, error });

    if (error) throw error;

    // Fetch coach info using proper helper
    const withCoachInfo = await Promise.all(
      (data || []).map(async (rel) => {
        const coachInfo = await getCoachInfo(rel.coach_id);
        return { ...rel, coach: coachInfo };
      })
    );

    console.log('[coachService.getMyCoaches] With coach info:', withCoachInfo);
    return withCoachInfo;
  },

  /**
   * Check if current user has any clients (is acting as coach)
   */
  async hasClients() {
    const user = await getCachedUser();
    if (!user) return false;

    const { count, error } = await supabase
      .from('coach_client_relationships')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', user.id)
      .eq('status', 'accepted');

    if (error) return false;
    return (count || 0) > 0;
  },

  /**
   * Check if current user has pending invitations
   */
  async hasPendingInvitations() {
    const user = await getCachedUser();
    if (!user) return false;

    const { count, error } = await supabase
      .from('coach_client_relationships')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', user.id)
      .eq('status', 'pending');

    if (error) return false;
    return (count || 0) > 0;
  },

  /**
   * Get client workouts (with permission check via RPC)
   */
  async getClientWorkouts(clientId, startDate = null, endDate = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_client_workouts', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_start_date: startDate,
        p_end_date: endDate,
      });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a workout for a client (coach mode)
   */
  async createClientWorkout(clientId, workoutData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    console.log('[coachService.createClientWorkout] Data received:', workoutData);

    const { data, error } = await supabase
      .rpc('coach_create_workout', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_date: workoutData.date,
        p_exercise_name: workoutData.exercise_name,
        p_exercise_type: workoutData.exercise_type || null,
        p_sets: workoutData.sets || null,
        p_reps: workoutData.reps || null,
        p_reps_detail: workoutData.reps_detail || null,
        p_weight: workoutData.weight || null,
        p_weight_unit: workoutData.weight_unit || null,
        p_duration_minutes: workoutData.duration_minutes || null,
        p_notes: workoutData.notes || null,
        p_muscle_group: workoutData.muscle_group || null,
      });

    if (error) throw error;
    return data;
  },

  /**
   * Update a workout for a client (coach mode)
   */
  async updateClientWorkout(clientId, workoutId, updates) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    console.log('[coachService.updateClientWorkout] Updates received:', updates);

    const { data, error } = await supabase
      .rpc('coach_update_workout', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_workout_id: workoutId,
        p_date: updates.date || null,
        p_exercise_name: updates.exercise_name || null,
        p_exercise_type: updates.exercise_type || null,
        p_sets: updates.sets || null,
        p_reps: updates.reps || null,
        p_reps_detail: updates.reps_detail || null,
        p_weight: updates.weight || null,
        p_weight_unit: updates.weight_unit || null,
        p_duration_minutes: updates.duration_minutes || null,
        p_notes: updates.notes || null,
        p_muscle_group: updates.muscle_group || null,
      });

    if (error) throw error;
    return data;
  },

  /**
   * Delete a workout for a client (coach mode)
   */
  async deleteClientWorkout(clientId, workoutId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('coach_delete_workout', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_workout_id: workoutId,
      });

    if (error) throw error;
    return data;
  },

  /**
   * Get client personal records (coach mode)
   */
  async getClientPRs(clientId, exerciseName = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_client_prs', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_exercise_name: exerciseName,
      });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get client summary stats (coach mode)
   */
  async getClientSummaryStats(clientId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_client_summary_stats', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
      });

    if (error) throw error;
    return data?.[0] || null;
  },

  /**
   * Get client workout frequency analytics (coach mode)
   */
  async getClientWorkoutFrequency(clientId, days = 30) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_client_workout_frequency', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_days: days,
      });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get client muscle group volume analytics (coach mode)
   */
  async getClientMuscleVolume(clientId, days = 7) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_client_muscle_volume', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_days: days,
      });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get client exercise progress analytics (coach mode)
   */
  async getClientExerciseProgress(clientId, exerciseName, days = 90) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_client_exercise_progress', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_exercise_name: exerciseName,
        p_days: days,
      });

    if (error) throw error;
    return data || [];
  },

  // =====================================================
  // CLIENT SCHEDULED WORKOUTS (coach mode)
  // =====================================================

  /**
   * Get ALL clients' fitness workouts (for coach's unified calendar view)
   */
  async getAllClientsWorkouts(startDate = null, endDate = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_all_clients_workouts', {
        p_coach_user_id: user.id,
        p_start_date: startDate,
        p_end_date: endDate,
      });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get ALL clients' scheduled workouts (for coach's own calendar view)
   */
  async getAllClientsScheduledWorkouts(startDate = null, endDate = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_all_clients_scheduled_workouts', {
        p_coach_user_id: user.id,
        p_start_date: startDate,
        p_end_date: endDate,
      });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get client scheduled workouts (coach mode)
   */
  async getClientScheduledWorkouts(clientId, startDate = null, endDate = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_coach_client_scheduled_workouts', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_start_date: startDate,
        p_end_date: endDate,
      });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a scheduled workout for a client (coach mode)
   */
  async createClientScheduledWorkout(clientId, workoutData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('coach_create_scheduled_workout', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_scheduled_date: workoutData.scheduled_date,
        p_scheduled_time: workoutData.scheduled_time,
        p_duration_minutes: workoutData.duration_minutes || 60,
        p_exercise_type: workoutData.exercise_type || 'fitness',
        p_muscle_group: workoutData.muscle_group || null,
        p_notes: workoutData.notes || null,
        p_planned_exercises: workoutData.planned_exercises || null,
      });

    if (error) throw error;
    return data;
  },

  /**
   * Update a scheduled workout for a client (coach mode)
   */
  async updateClientScheduledWorkout(clientId, workoutId, updates) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('coach_update_scheduled_workout', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_workout_id: workoutId,
        p_scheduled_date: updates.scheduled_date || null,
        p_scheduled_time: updates.scheduled_time || null,
        p_duration_minutes: updates.duration_minutes || null,
        p_exercise_type: updates.exercise_type || null,
        p_muscle_group: updates.muscle_group || null,
        p_notes: updates.notes || null,
        p_status: updates.status || null,
        p_planned_exercises: updates.planned_exercises || null,
        p_coach_participating: updates.coach_participating ?? null,
      });

    if (error) throw error;
    return data;
  },

  /**
   * Delete a scheduled workout for a client (coach mode)
   */
  async deleteClientScheduledWorkout(clientId, workoutId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('coach_delete_scheduled_workout', {
        p_coach_user_id: user.id,
        p_client_id: clientId,
        p_workout_id: workoutId,
      });

    if (error) throw error;
    return data;
  },
};

// =====================================================
// COACH NOTES MANAGEMENT
// =====================================================

export const coachNotes = {
  /**
   * Add a note to a client
   */
  async create(clientId, noteData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    // Get the relationship
    const { data: relationship, error: relError } = await supabase
      .from('coach_client_relationships')
      .select('id')
      .eq('coach_id', user.id) // Now directly using user.id
      .eq('client_id', clientId)
      .eq('status', 'accepted')
      .single();

    if (relError) throw new Error('No active relationship with this client');

    const { data, error } = await supabase
      .from('coach_notes')
      .insert({
        relationship_id: relationship.id,
        coach_id: user.id, // Now user.id, not coach_profiles.id
        client_id: clientId,
        workout_id: noteData.workoutId || null,
        workout_date: noteData.workoutDate || null,
        note_type: noteData.noteType || 'feedback',
        content: noteData.content,
        is_pinned: noteData.isPinned || false,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update a note
   */
  async update(noteId, noteData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_notes')
      .update({
        content: noteData.content,
        note_type: noteData.noteType,
        is_pinned: noteData.isPinned,
        workout_date: noteData.workoutDate,
      })
      .eq('id', noteId)
      .eq('coach_id', user.id) // Verify ownership
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a note
   */
  async delete(noteId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('coach_notes')
      .delete()
      .eq('id', noteId)
      .eq('coach_id', user.id); // Verify ownership

    if (error) throw error;
  },

  /**
   * Get notes written by current user for a specific client
   */
  async getForClient(clientId) {
    const user = await getCachedUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('coach_notes')
      .select('*')
      .eq('coach_id', user.id) // Now directly using user.id
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get all notes received from coaches (for the client)
   */
  async getMyNotes() {
    const user = await getCachedUser();
    if (!user) return [];

    // First get notes
    const { data: notes, error } = await supabase
      .from('coach_notes')
      .select('*')
      .eq('client_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!notes?.length) return [];

    // Fetch coach info for each unique coach
    const coachIds = [...new Set(notes.map((n) => n.coach_id))];
    const coachInfoMap = {};

    await Promise.all(
      coachIds.map(async (coachId) => {
        coachInfoMap[coachId] = await getCoachInfo(coachId);
      })
    );

    // Attach coach info to notes
    return notes.map((note) => ({
      ...note,
      coach: coachInfoMap[note.coach_id],
    }));
  },

  /**
   * Get unread notes count for the current user
   */
  async getUnreadCount() {
    const user = await getCachedUser();
    if (!user) return 0;

    const { data, error } = await supabase
      .rpc('get_unread_coach_notes_count', { p_user_id: user.id });

    if (error) return 0;
    return data || 0;
  },

  /**
   * Mark a note as read
   */
  async markAsRead(noteId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_notes')
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq('id', noteId)
      .eq('client_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Mark all notes as read
   */
  async markAllAsRead() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('coach_notes')
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq('client_id', user.id)
      .eq('is_read', false);

    if (error) throw error;
  },
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Get client basic info (for coaches) - uses RPC to bypass RLS
 */
async function getClientInfo(clientId) {
  const { data, error } = await supabase
    .rpc('get_client_basic_info', {
      p_client_id: clientId,
    });

  if (error || !data || data.length === 0) {
    console.error('Error fetching client info:', error);
    return { id: clientId, email: 'Unknown' };
  }

  // RPC returns array, take first element
  return data[0];
}

/**
 * Get coach info (profile email + optional coach_profiles data) - uses RPC to bypass RLS
 */
async function getCoachInfo(userId) {
  const { data, error } = await supabase
    .rpc('get_coach_basic_info', {
      p_coach_id: userId,
    });

  if (error || !data || data.length === 0) {
    console.error('Error fetching coach info:', error);
    return {
      id: userId,
      email: 'Unknown',
      display_name: 'Unknown',
      has_profile: false,
      bio: null,
      specializations: [],
      coach_type: 'fitness',
    };
  }

  // RPC returns array, take first element
  return data[0];
}

// =====================================================
// EXPORT ALL
// =====================================================

export default {
  coachProfile,
  userSearch,
  clientRelationships,
  coachRelationships,
  coachNotes,
};
